import React, { useEffect, useState } from 'react';
import { css } from 'emotion';
import { neutralBorder } from '@root/src/lib/pillars';
import { Section } from '@root/src/web/components/Section';
import { SubNav } from '@root/src/web/components/SubNav/SubNav';
import { brandBackground, brandLine } from '@guardian/src-foundations/palette';
import libDebounce from 'lodash.debounce';
import { decideTheme } from '@root/src/web/lib/decideTheme';
import { decideDesign } from '@root/src/web/lib/decideDesign';
import { getZIndex } from '@root/src/web/lib/getZIndex';
import { getCurrentPillar } from '@root/src/web/lib/layoutHelpers';
import { Nav as LazyNav } from './Nav';
import { Nav } from '../Nav';
const stickyStyle = (theme) => css `
	position: sticky;
	top: 0;
	${getZIndex('stickyNav')}
	background-color: white;
	box-shadow: 0 0 transparent, 0 0 transparent,
		1px 3px 6px ${neutralBorder(theme)};
`;
const fixedStyle = (theme, shouldDisplay) => css `
	width: 100%;
	position: fixed;
	top: 0;
	${getZIndex('stickyNav')}
	background-color: white;
	box-shadow: 0 0 transparent, 0 0 transparent,
		1px 3px 6px ${neutralBorder(theme)};

	display: ${shouldDisplay ? 'block' : 'none'};
`;
export const NavGroupEager = ({ capiData, navData, format, palette, }) => (React.createElement(React.Fragment, null,
    React.createElement(Section, { showSideBorders: true, borderColour: brandLine.primary, showTopBorder: false, padded: false, backgroundColour: brandBackground.primary },
        React.createElement(Nav, { nav: navData, format: {
                ...format,
                theme: getCurrentPillar(capiData),
            }, subscribeUrl: capiData.nav.readerRevenueLinks.header.subscribe, edition: capiData.editionId })),
    navData.subNavSections && (React.createElement(Section, { backgroundColour: palette.background.article, padded: false, sectionId: "sub-nav-root" },
        React.createElement(SubNav, { subNavSections: navData.subNavSections, currentNavLink: navData.currentNavLink, palette: palette })))));
const NavGroupLazy = ({ capiData, navData, format, palette, ID, }) => (React.createElement("div", null,
    React.createElement(Section, { showSideBorders: true, borderColour: brandLine.primary, showTopBorder: false, padded: false, backgroundColour: brandBackground.primary },
        React.createElement(LazyNav, { topLevelPillars: navData.topLevelPillars, format: format, subscribeUrl: capiData.nav.readerRevenueLinks.header.subscribe, edition: capiData.editionId, currentNavLinkTitle: navData.currentNavLink, ID: ID || 'lazy-nav', ajaxUrl: capiData.config.ajaxUrl })),
    navData.subNavSections && (React.createElement(Section, { backgroundColour: palette.background.article, padded: false, sectionId: "sub-nav-root" },
        React.createElement(SubNav, { subNavSections: navData.subNavSections, currentNavLink: navData.currentNavLink, palette: palette })))));
// StickyNavSimple is a basic, CSS only, sticky nav. The nav stays at the top of
// the viewport as the user scrolls past it's initial placement.
//
// *Note:* this uses position:sticky, which only works if the parent element is
// scrollable and has a fixed height.
export const StickyNavSimple = ({ capiData, navData, palette, format, }) => {
    const theme = decideTheme({
        pillar: capiData.pillar,
        design: decideDesign(capiData.designType, capiData.tags),
    });
    return (React.createElement("div", { className: stickyStyle(theme) },
        React.createElement(NavGroupLazy, { capiData: capiData, navData: navData, palette: palette, format: format, ID: "sticky-nav-simple" })));
};
// StickyNavBackScroll reappears fixed to top of viewport if below initial buffer and user
// is scrolling back. The idea is that scrolling back up may indicate intent to
// reach nav.
export const StickyNavBackscroll = ({ capiData, navData, format, palette, }) => {
    const initialState = { shouldFix: false, scrollY: 0 };
    const [state, setState] = useState(initialState);
    const pillar = decideTheme({
        pillar: capiData.pillar,
        design: decideDesign(capiData.designType, capiData.tags),
    });
    useEffect(() => {
        const handle = () => {
            setState((oldState) => {
                const buffer = 300;
                const newY = window.scrollY;
                const goingBack = newY < oldState.scrollY;
                const beforeRange = newY < buffer;
                return {
                    shouldFix: goingBack && !beforeRange,
                    scrollY: newY,
                };
            });
        };
        window.addEventListener('scroll', libDebounce(handle, 20), {
            passive: true,
        });
        return () => {
            window.removeEventListener('scroll', handle);
        };
    }, []);
    return (React.createElement(React.Fragment, null,
        React.createElement(NavGroupLazy, { capiData: capiData, navData: navData, palette: palette, format: format, ID: "nav" }),
        React.createElement("div", { className: fixedStyle(pillar, state.shouldFix) },
            React.createElement(NavGroupLazy, { capiData: capiData, navData: navData, palette: palette, format: format, ID: "sticky-nav-backscroll" }))));
};
